// ─────────────────────────────────────────────────────────────────────────
// Marsad GCC Markets — pre-built bundle source.
// Extracted from gcc.html's inline <script type="text/babel"> block on
// 2026-04-15. Do NOT edit the HTML's inline script — edit THIS file and
// run `npm run build:gcc` (scripts/build-gcc.mjs) which rebuilds
// /gcc.bundle.js and /gcc.css before deploy.
//
// React + ReactDOM are imported from npm and bundled into the output
// (no more unpkg CDN or in-browser Babel). The original code used global
// `React` / `ReactDOM` references + a destructured hook set, so we keep
// that shape by exposing them as module-level bindings below.
// ─────────────────────────────────────────────────────────────────────────
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
// Expose as globals for any legacy inline callsites / devtools.
if (typeof window !== 'undefined') {
  window.React = window.React || React;
  window.ReactDOM = window.ReactDOM || ReactDOM;
}

(async function() {

const { useState, useEffect, useRef, useCallback, useMemo } = React;
const GCC_API = '/.netlify/functions/gcc';
const REFRESH_MS = 60000;

// ── i18n: bilingual EN/AR ────────────────────────────────────
// Stored language preference in localStorage. Translation map
// covers UI chrome only (labels, tabs, section headers). Stock
// names and ticker codes stay in source language since mubasher
// doesn't return Arabic equivalents in the hub payload.
const I18N = {
  en: {
    title: 'GCC Markets',
    subtitle: 'GCC stock indices, news, and corporate events',
    search: 'Search stocks across all GCC markets...',
    overview: 'Overview',
    heatmap: 'Heatmap',
    news: '📰 News',
    calendar: '📅 Calendar',
    disclosures: '📋 Disclosures',
    watchlist: '★ Watchlist',
    indices: 'All Indices & Charts',
    updated: 'updated',
    ago: 'ago',
    cardsDelayed: 'cards ~15min delayed',
    markets: 'markets',
    live: 'live',
    closed: 'closed',
    open: 'OPEN',
    closedShort: 'CLOSED',
    breadth: 'Breadth',
    volume: 'Volume',
    turnover: 'Turnover',
    topMover: 'TOP MOVER',
    indicesCount: 'indices',
    view: 'View',
    sentiment: 'Market Sentiment',
    adv: 'adv',
    advancers: 'Adv',
    decliners: 'Dec',
    vol: 'Vol',
    watchlistEmpty: 'Your watchlist is empty',
    watchlistHint: 'Add stocks to your watchlist from any stock page to track them here.',
    newsHint: 'Latest headlines across all GCC markets. Updated continuously from mubasher.info.',
    newsEmpty: 'No headlines available',
    calendarHint: 'Upcoming shareholder meetings, corporate actions, and dividends across GCC markets.',
    calendarEmpty: 'No upcoming events',
    disclosuresHint: 'Regulatory disclosures filed with GCC exchanges. Titles available in Arabic and English, refreshed daily.',
    disclosuresEmpty: 'No disclosures available',
    showInEnglish: 'Show in English',
    showInArabic: 'Show in Arabic',
    indicesHint: 'Interactive TradingView charts for every available GCC index — organized by country.',
    heatmapHint: 'GCC markets sized by trading volume. Click any market to view details.',
    date: 'Date',
    market: 'Market',
    company: 'Company',
    event: 'Event',
    agenda: 'Agenda',
    failedLoad: 'Failed to load market data',
    retry: 'Retry',
    loading: 'Loading…',
  },
  ar: {
    title: 'أسواق الخليج',
    subtitle: 'مؤشرات أسواق الخليج، الأخبار، والأحداث المؤسسية',
    search: 'ابحث عن سهم في جميع أسواق الخليج...',
    overview: 'نظرة عامة',
    heatmap: 'الخريطة الحرارية',
    news: '📰 الأخبار',
    calendar: '📅 التقويم',
    disclosures: '📋 الإفصاحات',
    watchlist: '★ المفضلة',
    indices: 'جميع المؤشرات والرسوم البيانية',
    updated: 'تحديث',
    ago: 'مضت',
    cardsDelayed: 'البطاقات متأخرة ١٥ دقيقة',
    markets: 'سوق',
    live: 'مفتوح',
    closed: 'مغلق',
    open: 'مفتوح',
    closedShort: 'مغلق',
    breadth: 'نطاق السوق',
    volume: 'الكمية',
    turnover: 'القيمة',
    topMover: 'الأكثر ارتفاعًا',
    indicesCount: 'مؤشر',
    view: 'عرض',
    sentiment: 'مزاج السوق',
    adv: 'صاعد',
    advancers: 'صاعد',
    decliners: 'هابط',
    vol: 'كمية',
    watchlistEmpty: 'قائمة المفضلة فارغة',
    watchlistHint: 'أضف الأسهم إلى المفضلة من صفحة أي سهم لمتابعتها هنا.',
    newsHint: 'آخر الأخبار من جميع أسواق الخليج. تحديث مستمر من mubasher.info.',
    newsEmpty: 'لا توجد أخبار متاحة',
    calendarHint: 'اجتماعات الجمعيات العمومية، والأحداث المؤسسية، والأرباح القادمة في أسواق الخليج.',
    calendarEmpty: 'لا توجد أحداث قادمة',
    disclosuresHint: 'الإفصاحات التنظيمية المقدمة إلى بورصات الخليج. العناوين متاحة بالعربية والإنجليزية، تُحدَّث يوميًا.',
    disclosuresEmpty: 'لا توجد إفصاحات متاحة',
    showInEnglish: 'عرض بالإنجليزية',
    showInArabic: 'عرض بالعربية',
    indicesHint: 'رسوم بيانية تفاعلية من TradingView لكل مؤشرات الخليج المتاحة — مصنفة حسب الدولة.',
    heatmapHint: 'أسواق الخليج بأحجام تعكس حجم التداول. انقر على أي سوق لعرض التفاصيل.',
    date: 'التاريخ',
    market: 'السوق',
    company: 'الشركة',
    event: 'الحدث',
    agenda: 'جدول الأعمال',
    failedLoad: 'تعذّر تحميل بيانات السوق',
    retry: 'إعادة المحاولة',
    loading: 'جارٍ التحميل…',
  },
};
function getInitialLang() {
  try {
    var stored = localStorage.getItem('marsad_lang');
    if (stored === 'ar' || stored === 'en') return stored;
  } catch (e) {}
  // Default to Arabic for GCC users (browser language detection)
  var nav = (navigator.language || 'en').toLowerCase();
  return nav.startsWith('ar') ? 'ar' : 'en';
}

// ── Marsad logo ──────────────────────────────────────────────
const RAYS = ['#00F0FF','#FF2BD6','#B5FF1C','#FF7A00','#FF0066','#A020F0','#00A2FF','#00FFA3'];
function Logo({ size }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" width={size||32} height={size||32} style={{flexShrink:0,display:'block'}}>
      <g strokeWidth="18" strokeLinecap="round">
        {RAYS.map(function(c,i){return <g key={i} transform={'rotate('+(i*45)+' 200 200)'}><line x1="350" y1="200" x2="246.54" y2="200" stroke={c}/></g>;})}
      </g>
    </svg>
  );
}

// ── Formatting ───────────────────────────────────────────────
function fN(n,d){if(n==null||!isFinite(n))return '—';d=d!=null?d:2;return Number(n).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d});}
function fP(n){if(n==null||!isFinite(n))return '—';return (n>=0?'+':'')+Number(n).toFixed(2)+'%';}
function fB(n){if(n==null||!isFinite(n))return '—';var a=Math.abs(n);if(a>=1e9)return(n/1e9).toFixed(1)+'B';if(a>=1e6)return(n/1e6).toFixed(1)+'M';if(a>=1e3)return(n/1e3).toFixed(1)+'K';return String(n);}
function cc(n){if(n==null||!isFinite(n))return 'gcc-mu';return n>0.0001?'gcc-up':n<-0.0001?'gcc-dn':'gcc-mu';}

var MARKET_SHORT={'BK':'kw','TDWL':'sa','DFM':'dfm','ADX':'adx','QE':'qa','BB':'bh','MSM':'om'};

// ── TradingView Market Overview (SINGLE widget, loaded lazily) ──
function TVMarketOverview({ visible }) {
  var ref = useRef(null);
  var loaded = useRef(false);

  useEffect(function() {
    if (!visible || loaded.current) return;
    loaded.current = true;
    var el = ref.current;
    if (!el) return;

    var script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      colorTheme: 'dark',
      dateRange: '1M',
      showChart: true,
      locale: 'en',
      isTransparent: true,
      showSymbolLogo: true,
      showFloatingTooltip: true,
      width: '100%',
      height: '520',
      plotLineColorGrowing: 'rgba(74,222,128,1)',
      plotLineColorFalling: 'rgba(248,113,113,1)',
      gridLineColor: 'rgba(255,255,255,0.04)',
      scaleFontColor: 'rgba(148,163,184,1)',
      belowLineFillColorGrowing: 'rgba(74,222,128,0.06)',
      belowLineFillColorFalling: 'rgba(248,113,113,0.06)',
      belowLineFillColorGrowingBottom: 'rgba(74,222,128,0)',
      belowLineFillColorFallingBottom: 'rgba(248,113,113,0)',
      symbolActiveColor: 'rgba(0,240,255,0.08)',
      tabs: [
        { title: 'Kuwait 🇰🇼', symbols: [
          { s:'KSE:BKA',d:'All Share' },{ s:'KSE:BKP',d:'Premier Market' },{ s:'KSE:BKM',d:'Main Market' },{ s:'KSE:BKM50',d:'Main 50' },
        ]},
        { title: 'Saudi 🇸🇦', symbols: [
          { s:'TADAWUL:TASI',d:'All Share (TASI)' },{ s:'TADAWUL:TBNI',d:'Banks' },{ s:'TADAWUL:TENI',d:'Energy' },
          { s:'TADAWUL:TCGI',d:'Materials' },{ s:'TADAWUL:TCPI',d:'Capital Goods' },{ s:'TADAWUL:TRLI',d:'Retail' },
          { s:'TADAWUL:TPBI',d:'Pharma & Biotech' },{ s:'TADAWUL:TFBI',d:'Food & Beverages' },{ s:'TADAWUL:THEI',d:'Health Care' },
          { s:'TADAWUL:TISI',d:'Insurance' },{ s:'TADAWUL:TDAI',d:'Diversified Financials' },{ s:'TADAWUL:TFSI',d:'Food & Staples Retail' },
          { s:'TADAWUL:TDFI',d:'Transportation' },{ s:'TADAWUL:TCSI',d:'Consumer Services' },
        ]},
        { title: 'UAE 🇦🇪', symbols: [
          { s:'DFM:DFMGI',d:'Dubai General' },{ s:'DFM:DFMSI',d:'Dubai Shari\'a' },{ s:'DFM:DFMREI',d:'Dubai Real Estate' },
          { s:'ADX:FADGI',d:'Abu Dhabi General' },{ s:'ADX:FADX15',d:'Abu Dhabi Top 15' },{ s:'ADX:FADBMI',d:'Abu Dhabi Basic Materials' },{ s:'ADX:FADREI',d:'Abu Dhabi Real Estate' },
        ]},
        { title: 'Qatar 🇶🇦', symbols: [
          { s:'QSE:GNRI',d:'QE Index (Top 20)' },{ s:'QSE:QEAS',d:'QE All Share' },
        ]},
        { title: 'Bahrain 🇧🇭', symbols: [
          { s:'BAHRAIN:BHBX',d:'All Share' },{ s:'BAHRAIN:BIX',d:'Islamic' },
        ]},
        { title: 'Blue Chips', symbols: [
          { s:'TADAWUL:2222',d:'Aramco' },{ s:'TADAWUL:1180',d:'Al Rajhi Bank' },{ s:'QSE:QNBK',d:'QNB Group' },
          { s:'ADX:FAB',d:'First Abu Dhabi Bank' },{ s:'KSE:NBK',d:'National Bank of Kuwait' },{ s:'KSE:ZAIN',d:'Zain' },
          { s:'DFM:EMAAR',d:'Emaar Properties' },{ s:'DFM:DIB',d:'Dubai Islamic Bank' },
        ]},
      ],
    });
    el.appendChild(script);
  }, [visible]);

  return (
    <div className="tv-overview-wrap" style={{minHeight:visible?520:0,display:visible?'block':'none'}}>
      <div ref={ref} style={{width:'100%',height:'520px'}}></div>
    </div>
  );
}

// ── Live ticker tape (TradingView) ───────────────────────────
// Streams live prices for all GCC indices with TradingView coverage.
// Uses direct iframe embed (more reliable than script-injection).
// Note: Oman (MSX30) is not on TradingView.
// Perf: the iframe is heavy (pulls ~200KB TV JS + a websocket) so we
// defer mounting until after the first paint — either when the browser
// reports idle, when the viewport is visible via IntersectionObserver,
// or after a 1200ms fallback so it never blocks LCP on the overview tab.
function TVTickerTape() {
  var [ready, setReady] = useState(false);
  var boxRef = useRef(null);
  useEffect(function() {
    var mounted = true;
    function arm() { if (mounted) setReady(true); }
    // 1. Prefer requestIdleCallback (Chrome/Edge/Firefox).
    var rid = null;
    if (typeof window.requestIdleCallback === 'function') {
      rid = window.requestIdleCallback(arm, { timeout: 1500 });
    }
    // 2. IntersectionObserver — only mount when the placeholder is in view.
    var io = null;
    try {
      if (typeof IntersectionObserver !== 'undefined' && boxRef.current) {
        io = new IntersectionObserver(function(entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) { arm(); break; }
          }
        }, { rootMargin: '200px' });
        io.observe(boxRef.current);
      }
    } catch (e) {}
    // 3. Hard fallback so the ticker always appears on old browsers.
    var tm = setTimeout(arm, 1200);
    return function() {
      mounted = false;
      clearTimeout(tm);
      if (rid != null && typeof window.cancelIdleCallback === 'function') {
        try { window.cancelIdleCallback(rid); } catch (e) {}
      }
      if (io) { try { io.disconnect(); } catch (e) {} }
    };
  }, []);

  var config = {
    symbols: [
      // Working index symbols on TV ticker-tape
      { proName: 'KSE:BKA',       title: 'Kuwait' },
      { proName: 'ADX:FADGI',     title: 'Abu Dhabi' },
      // Blue-chip proxies for markets whose index symbols fail on
      // ticker-tape (Saudi TASI, Dubai DFMGI, Qatar GNRI, Bahrain BHBX).
      // These stocks stream cleanly and anchor each market in the tape.
      { proName: 'TADAWUL:2222',  title: 'Aramco · Saudi' },
      { proName: 'TADAWUL:1180',  title: 'Al Rajhi · Saudi' },
      { proName: 'DFM:EMAAR',     title: 'Emaar · Dubai' },
      { proName: 'DFM:DIB',       title: 'DIB · Dubai' },
      { proName: 'QSE:QNBK',      title: 'QNB · Qatar' },
      { proName: 'BAHRAIN:BBK',   title: 'BBK · Bahrain' },
      // Kuwait & Abu Dhabi extras
      { proName: 'KSE:NBK',       title: 'NBK' },
      { proName: 'KSE:ZAIN',      title: 'Zain' },
      { proName: 'ADX:FAB',       title: 'FAB' },
    ],
    showSymbolLogo: true,
    colorTheme: 'dark',
    isTransparent: true,
    displayMode: 'adaptive',
    locale: 'en',
    width: '100%',
    height: 46
  };
  var src = 'https://s.tradingview.com/embed-widget/ticker-tape/?locale=en#' +
            encodeURIComponent(JSON.stringify(config));

  return (
    <div ref={boxRef} style={{margin:'0 0 12px 0',borderRadius:8,overflow:'hidden',border:'1px solid rgba(255,255,255,0.06)',minHeight:46,background:'rgba(255,255,255,0.02)'}}>
      {ready ? (
        <iframe
          title="GCC live ticker"
          src={src}
          loading="lazy"
          style={{width:'100%',height:'46px',border:0,display:'block',background:'transparent'}}
          allow="clipboard-write"
        />
      ) : null}
    </div>
  );
}

// ── Macro strip (2026-04-15) ─────────────────────────────────
// Pinned above the index cards: 7 tiles of cross-asset macro context a GCC
// investor cares about. Data is served by /.netlify/functions/gcc?action=macros
// which Promise.allSettled's a fixed Yahoo quote list. We poll on the same
// 60s cadence as the hub during market hours, and pause when the tab is
// hidden. Failed fetches leave the tile showing the last-known value with
// a dimmer opacity so the strip layout never jumps.
function formatMacroPrice(price, unit) {
  if (price == null || !isFinite(price)) return '—';
  if (unit === 'pct') return price.toFixed(2) + '%';
  if (unit === 'fx')  return price.toFixed(4);
  if (unit === 'idx') return price.toFixed(2);
  // Default: USD-ish (Brent, WTI, Gold, BTC) — BTC is big so compact once ≥10k.
  var abs = Math.abs(price);
  if (abs >= 10000) return (price / 1000).toFixed(2) + 'K';
  if (abs >= 100)   return price.toFixed(2);
  return price.toFixed(3);
}
function MacrosStrip() {
  var [macros, setMacros] = useState(null);
  var [stale, setStale]   = useState(false);
  useEffect(function() {
    var cancelled = false;
    function load() {
      fetch(GCC_API + '?action=macros')
        .then(function(r){ return r.ok ? r.json() : null; })
        .then(function(j){
          if (cancelled) return;
          if (j && Array.isArray(j.macros)) { setMacros(j.macros); setStale(false); }
          else { setStale(true); }
        })
        .catch(function(){ if (!cancelled) setStale(true); });
    }
    load();
    var id = setInterval(load, 60000);
    function onVis(){ if (document.visibilityState === 'visible') load(); }
    document.addEventListener('visibilitychange', onVis);
    return function() {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
  // Skeleton row: 7 pulsing placeholders until the first fetch resolves,
  // so the strip occupies space immediately (avoids layout shift).
  var rows = macros || [
    { key:'brent', label:'Brent' }, { key:'wti', label:'WTI' }, { key:'gold', label:'Gold' },
    { key:'dxy', label:'DXY' }, { key:'us10y', label:'US 10Y' }, { key:'sp500', label:'S&P 500' },
    { key:'btc', label:'BTC' },
  ];
  return (
    <div className="mb-3" style={{opacity: stale ? 0.6 : 1, transition: 'opacity 0.3s'}}>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
        {rows.map(function(m) {
          var isUp   = m.changePct != null && m.changePct > 0.0001;
          var isDown = m.changePct != null && m.changePct < -0.0001;
          var colorCls = isUp ? 'text-emerald-400' : isDown ? 'text-rose-400' : 'text-slate-400';
          var pctStr = m.changePct == null ? '—' : (m.changePct >= 0 ? '+' : '') + m.changePct.toFixed(2) + '%';
          return (
            <div key={m.key}
                 className="gcc-card px-3 py-2 flex flex-col justify-between"
                 style={{minHeight:56}}
                 title={m.symbol ? 'Yahoo: ' + m.symbol : ''}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{m.label}</span>
                <span className={'text-[10px] font-mono tabular-nums ' + colorCls}>{pctStr}</span>
              </div>
              <div className="text-sm font-mono tabular-nums text-slate-100 mt-1">
                {macros ? formatMacroPrice(m.price, m.unit) : <span className="inline-block h-3 w-12 bg-white/5 rounded animate-pulse"/>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Search (pre-loads compact index on mount) ────────────────
var _searchCache = null;

function GlobalSearch({ t }) {
  t = t || I18N.en;
  var [query, setQuery] = useState('');
  var [focused, setFocused] = useState(false);
  var [allStocks, setAllStocks] = useState(_searchCache);
  var [loading, setLoading] = useState(false);
  var wrapRef = useRef(null);
  var inputRef = useRef(null);

  useEffect(function() {
    function handleClick(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocused(false); }
    document.addEventListener('mousedown', handleClick);
    return function() { document.removeEventListener('mousedown', handleClick); };
  }, []);

  useEffect(function() {
    if (_searchCache) { setAllStocks(_searchCache); return; }
    setLoading(true);
    fetch(GCC_API + '?action=search')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (d && d.stocks) {
          _searchCache = d.stocks;
          setAllStocks(d.stocks);
        }
      })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }, []);

  var [activeIdx, setActiveIdx] = useState(-1);
  function handleKeyDown(e) {
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(function(i) { return Math.min(i + 1, results.length - 1); }); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(function(i) { return Math.max(i - 1, 0); }); }
    else if (e.key === 'Enter' && activeIdx >= 0 && results[activeIdx]) {
      e.preventDefault();
      var s = results[activeIdx];
      var c = (s.c || '').split('`')[0].split("'")[0].trim();
      window.location.href = '/markets/' + s.m + '/' + c;
    } else if (e.key === 'Escape') { setFocused(false); }
  }

  useEffect(function() { setActiveIdx(-1); }, [query]);

  var results = useMemo(function() {
    if (!query || !allStocks) return [];
    var q = query.toUpperCase().trim();
    if (q.length < 1) return [];
    var exact = [];
    var codeStart = [];
    var codeContains = [];
    var nameMatch = [];
    for (var i = 0; i < allStocks.length; i++) {
      var s = allStocks[i];
      var code = (s.c || '').toUpperCase();
      var name = (s.n || '').toUpperCase();
      if (code === q) { exact.push(s); }
      else if (code.indexOf(q) === 0) { codeStart.push(s); }
      else if (code.indexOf(q) >= 0) { codeContains.push(s); }
      else if (name.indexOf(q) >= 0) { nameMatch.push(s); }
    }
    return exact.concat(codeStart, codeContains, nameMatch).slice(0, 20);
  }, [query, allStocks]);

  return (
    <div ref={wrapRef} className="search-wrap w-full max-w-lg">
      <div className="relative">
        <input ref={inputRef} type="text" value={query}
          onChange={function(e) { setQuery(e.target.value); }}
          onFocus={function() { setFocused(true); }}
          onKeyDown={handleKeyDown}
          placeholder={t.search}
          className="w-full bg-[#13131C] border border-white/8 rounded-xl px-4 py-2.5 pl-10 text-sm text-white placeholder-slate-600 outline-none focus:border-cyan-500/30 transition-colors"
        />
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
        {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 gcc-breath">Loading…</span>}
      </div>
      {focused && query.length >= 1 && results.length > 0 && (
        <div className="search-results">
          {results.map(function(s, i) {
            var c = (s.c || '').split('`')[0].split("'")[0].trim();
            return (
              <a key={s.m + '-' + c} href={'/markets/' + s.m + '/' + c}
                className={'search-result-item' + (i === activeIdx ? ' bg-white/[0.06]' : '')}
                onMouseEnter={function() { setActiveIdx(i); }}>
                <div className="flex items-center gap-2.5">
                  <span style={{fontSize: 15}}>{s.f}</span>
                  <div>
                    <span className="text-sm font-semibold text-white font-mono">{c}</span>
                    <span className="text-xs text-slate-500 ml-2">{s.n || ''}</span>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500">{s.l}</div>
              </a>
            );
          })}
        </div>
      )}
      {focused && query.length >= 1 && results.length === 0 && allStocks && (
        <div className="search-results">
          <div className="px-4 py-3 text-xs text-slate-500">No stocks found matching "{query}"</div>
        </div>
      )}
    </div>
  );
}

// ── Card component (market with top movers) ───────────────────
// Pure-SVG sparkline. Inputs: an array of close prices (any length).
// Renders a smooth path + a faint area-fill underneath, color tinted by
// whether the series ended higher or lower than it started. No deps.
// Designed for inline use inside the index card (~140×32 px).
function Sparkline({ closes, color }) {
  if (!Array.isArray(closes) || closes.length < 2) {
    // Skeleton placeholder so the card layout doesn't jump in/out.
    return <div style={{height:32,opacity:0.4}}><div className="h-full w-full bg-white/5 rounded animate-pulse"/></div>;
  }
  var W = 140, H = 32, PAD = 2;
  var min = Math.min.apply(null, closes);
  var max = Math.max.apply(null, closes);
  var range = (max - min) || 1;
  var n = closes.length;
  var pts = closes.map(function(c, i) {
    var x = PAD + (i * (W - PAD * 2)) / (n - 1);
    var y = PAD + (H - PAD * 2) * (1 - (c - min) / range);
    return [x, y];
  });
  var line = pts.map(function(p, i) { return (i === 0 ? 'M' : 'L') + p[0].toFixed(2) + ',' + p[1].toFixed(2); }).join(' ');
  var area = line + ' L' + pts[n-1][0].toFixed(2) + ',' + (H - PAD).toFixed(2) +
             ' L' + pts[0][0].toFixed(2) + ',' + (H - PAD).toFixed(2) + ' Z';
  var trendUp = closes[n-1] >= closes[0];
  var stroke = color || (trendUp ? '#4ade80' : '#f87171');
  var fill   = trendUp ? 'rgba(74,222,128,0.10)' : 'rgba(248,113,113,0.10)';
  var gradId = 'sg-' + Math.random().toString(36).slice(2, 8);
  return (
    <svg viewBox={'0 0 ' + W + ' ' + H} preserveAspectRatio="none"
         style={{width:'100%', height:32, display:'block'}}
         aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.18"/>
          <stop offset="100%" stopColor={stroke} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill={'url(#' + gradId + ')'}/>
      <path d={line} fill="none" stroke={stroke} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}

function Card({ m, t, spark }) {
  t = t || I18N.en;
  var up = (m.changePct||0) >= 0;
  var color = up ? '#4ade80' : (m.changePct < 0 ? '#f87171' : '#9ca3af');
  var href = '/markets/' + (MARKET_SHORT[m.market]||m.market.toLowerCase());

  var rangePos = null;
  if (m.high != null && m.low != null && m.high > m.low && m.value != null) {
    rangePos = Math.max(0, Math.min(100, ((m.value - m.low) / (m.high - m.low)) * 100));
  }

  var idxCount = m.tv && m.tv.allIndices ? m.tv.allIndices.length : 0;

  // Top mover highlight
  var topMover = m.topGainers && m.topGainers.length > 0 ? m.topGainers[0] : null;

  return (
    <a href={href} className="gcc-card block p-4" style={{textDecoration:'none',color:'inherit'}}>
      <div className="flex items-center gap-2.5 mb-3">
        <span style={{fontSize:20}}>{m.flag}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-400">{m.short}</span>
            <span className="flex items-center gap-1.5" style={{fontSize:10,color:m.isOpen?'#4ade80':'#64748b'}}>
              <span className={m.isOpen?'gcc-dot-live':'gcc-dot-closed'}></span>
              {m.isOpen?t.open:t.closedShort}
            </span>
          </div>
          <div className="text-[13px] text-slate-300 font-medium truncate">{m.label}</div>
        </div>
      </div>

      <div className="flex items-baseline justify-between">
        <div className="font-mono tabular-nums text-[22px] font-semibold text-white" style={{letterSpacing:'-0.02em'}}>
          {fN(m.value)}
        </div>
        <div className="text-right">
          <div className="font-mono tabular-nums text-[13px] font-semibold" style={{color:color}}>{fP(m.changePct)}</div>
          <div className="font-mono tabular-nums text-[10px]" style={{color:color,opacity:0.7}}>
            {m.change!=null?((m.change>=0?'+':'')+fN(m.change)):''}
          </div>
        </div>
      </div>

      {/* 1-month sparkline (rendered as inline SVG so there's no chart lib
          weight). Color matches the % change tint; if no series is available
          yet (sparklines load asynchronously after the hub) the component
          shows a pulsing skeleton at the same height to avoid layout shift. */}
      <div className="mt-2.5">
        <Sparkline closes={spark} color={color}/>
      </div>

      {rangePos != null && (
        <div className="mt-2.5">
          <div className="flex justify-between text-[9px] text-slate-600 font-mono mb-0.5">
            <span>{fN(m.low)}</span><span>{fN(m.high)}</span>
          </div>
          <div className="range-bar">
            <div style={{position:'absolute',inset:0,borderRadius:2,background:'linear-gradient(90deg,rgba(248,113,113,0.2),rgba(156,163,175,0.1),rgba(74,222,128,0.2))'}}></div>
            <div className="range-dot" style={{left:rangePos+'%'}}></div>
          </div>
        </div>
      )}

      <div className="mt-3 pt-2.5 border-t border-white/5 grid grid-cols-3 gap-2 text-[10px]">
        {m.breadth ? (
          <div>
            <div className="text-slate-600 mb-0.5">{t.breadth}</div>
            <div className="font-mono">
              <span className="gcc-up">{m.breadth.advancers||0}</span>
              <span className="text-slate-700 mx-0.5">/</span>
              <span className="gcc-dn">{m.breadth.decliners||0}</span>
              <span className="text-slate-700 mx-0.5">/</span>
              <span className="gcc-mu">{m.breadth.unchanged||0}</span>
            </div>
          </div>
        ) : <div></div>}
        <div>
          <div className="text-slate-600 mb-0.5">{t.volume}</div>
          <div className="font-mono text-slate-300">{fB(m.volume)}</div>
        </div>
        <div>
          <div className="text-slate-600 mb-0.5">{t.turnover}</div>
          <div className="font-mono text-slate-300">{fB(m.turnover)}</div>
        </div>
      </div>

      {topMover && (
        <div className="mt-2.5 pt-2.5 border-t border-white/5 bg-gradient-to-r from-green-500/5 to-transparent rounded p-2">
          <div className="text-[9px] text-slate-600 mb-1">{t.topMover}</div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-white truncate">{topMover.code||topMover.name}</span>
            <span className="font-mono font-bold gcc-up">{fP(topMover.changePct)}</span>
          </div>
        </div>
      )}

      <div className="mt-2.5 pt-2.5 border-t border-white/5 flex items-center justify-between">
        {idxCount > 1 && <span className="text-[9px] text-slate-600">{idxCount} {t.indicesCount}</span>}
        {!idxCount && <span></span>}
        <span className="text-[10px] text-cyan-400/80 font-semibold">{t.view} →</span>
      </div>
    </a>
  );
}

// ── Summary stats ────────────────────────────────────────────
function Summary({ markets, t }) {
  t = t || I18N.en;
  if (!markets || !markets.length) return null;
  var vol=0,to=0,adv=0,dec=0,live=0;
  markets.forEach(function(m){
    vol+=(m.volume||0);to+=(m.turnover||0);
    if(m.breadth){adv+=(m.breadth.advancers||0);dec+=(m.breadth.decliners||0);}
    if(m.isOpen)live++;
  });
  var total = adv + dec;
  var advPct = total > 0 ? (adv / total) * 100 : 50;

  return (
    <div className="space-y-4 mb-6">
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-[11px]">
        <span className="text-slate-500">{markets.length} {t.markets} <span className={live?'gcc-up':'gcc-mu'}>({live} {t.live})</span></span>
        <span className="text-slate-500">{t.vol} <span className="text-slate-300 font-mono">{fB(vol)}</span></span>
        <span className="text-slate-500">{t.turnover} <span className="text-slate-300 font-mono">{fB(to)}</span></span>
        <span className="text-slate-500">{t.advancers} <span className="gcc-up font-mono">{adv}</span> / {t.decliners} <span className="gcc-dn font-mono">{dec}</span></span>
      </div>

      {/* Market breadth visualization */}
      <div className="flex items-center gap-3 text-[11px]">
        <span className="text-slate-600">{t.sentiment}:</span>
        <div style={{flex:1,height:6,background:'rgba(255,255,255,0.06)',borderRadius:3,overflow:'hidden'}}>
          <div style={{width:advPct+'%',height:'100%',background:'linear-gradient(90deg,#4ade80,#22c55e)',transition:'width 0.3s'}}/>
        </div>
        <span className="font-mono text-slate-300 min-w-max">{advPct.toFixed(0)}% {t.adv}</span>
      </div>
    </div>
  );
}

// ── SectorHeatmap component ──────────────────────────────────
// Renders GCC sector sub-indices grouped by country. Tile background is
// color-scaled by |changePct| clamped to ±4% (saturating beyond that so a
// single -10% sector doesn't wash everything else gray). Each tile is a
// link to the sector sub-index page (future — right now just tel: hints).
// We deliberately don't size by turnover: sector sub-indices don't report
// turnover from mubasher's priceBar, so we'd be faking it. Equal-weight
// tiles keep the visualization honest — color carries all the signal.
const SECTOR_HEATMAP_CLAMP = 4; // ±4% saturates

function sectorTileColor(pct) {
  if (pct == null || !isFinite(pct)) return { bg:'rgba(148,163,184,0.06)', border:'rgba(148,163,184,0.18)', text:'#94a3b8' };
  var clamped = Math.max(-SECTOR_HEATMAP_CLAMP, Math.min(SECTOR_HEATMAP_CLAMP, pct));
  var intensity = Math.abs(clamped) / SECTOR_HEATMAP_CLAMP; // 0..1
  if (pct > 0.0001) {
    // green scale — 74,222,128
    return {
      bg:     'rgba(74,222,128,'  + (0.06 + intensity * 0.28).toFixed(3) + ')',
      border: 'rgba(74,222,128,'  + (0.18 + intensity * 0.30).toFixed(3) + ')',
      text:   intensity > 0.35 ? '#bbf7d0' : '#86efac',
    };
  }
  if (pct < -0.0001) {
    // red scale — 248,113,113
    return {
      bg:     'rgba(248,113,113,' + (0.06 + intensity * 0.28).toFixed(3) + ')',
      border: 'rgba(248,113,113,' + (0.18 + intensity * 0.30).toFixed(3) + ')',
      text:   intensity > 0.35 ? '#fecaca' : '#fca5a5',
    };
  }
  return { bg:'rgba(148,163,184,0.08)', border:'rgba(148,163,184,0.22)', text:'#cbd5e1' };
}

function SectorHeatmap({ sectorsByMarket, markets, t }) {
  // markets gives us flag + label + short for each country.
  // sectorsByMarket is { [marketCode]: [{code,label,value,changePct}, ...] }.
  if (!sectorsByMarket) {
    return <div className="text-slate-500 text-sm">Loading sector heatmap…</div>;
  }
  var anyData = Object.keys(sectorsByMarket).some(function(k){
    return (sectorsByMarket[k] || []).length > 0;
  });
  if (!anyData) {
    return (
      <div className="gcc-card p-6 text-center">
        <div className="text-slate-400 text-sm">No sector data available right now.</div>
        <div className="text-slate-500 text-xs mt-1">Sector sub-indices may be outside trading hours.</div>
      </div>
    );
  }

  // Compute advancers/decliners summary across all sectors for a quick stat.
  var allSectors = [];
  Object.keys(sectorsByMarket).forEach(function(mkt){
    (sectorsByMarket[mkt] || []).forEach(function(s){
      if (s.changePct != null && isFinite(s.changePct)) {
        allSectors.push({ market: mkt, sector: s });
      }
    });
  });
  var up = allSectors.filter(function(x){ return x.sector.changePct > 0.0001; }).length;
  var dn = allSectors.filter(function(x){ return x.sector.changePct < -0.0001; }).length;
  var fl = allSectors.length - up - dn;
  var bestSector  = allSectors.slice().sort(function(a,b){ return (b.sector.changePct||0) - (a.sector.changePct||0); })[0];
  var worstSector = allSectors.slice().sort(function(a,b){ return (a.sector.changePct||0) - (b.sector.changePct||0); })[0];

  // Render a block per country, iterating in the order of `markets` (which
  // mirrors GCC_MARKETS sequence in gcc.js). Skip markets that came back
  // empty from the sector fan-out.
  return (
    <React.Fragment>
      {/* Summary strip */}
      <div className="gcc-card p-4 mb-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-slate-500 uppercase tracking-wider">Sectors tracked</span>
            <span className="font-mono tabular-nums text-slate-100 font-semibold">{allSectors.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{background:'#4ade80'}}></span>
            <span className="text-slate-400">Up</span>
            <span className="font-mono tabular-nums text-emerald-300 font-semibold">{up}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{background:'#f87171'}}></span>
            <span className="text-slate-400">Down</span>
            <span className="font-mono tabular-nums text-rose-300 font-semibold">{dn}</span>
          </div>
          {fl > 0 && (
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full" style={{background:'#94a3b8'}}></span>
              <span className="text-slate-400">Flat</span>
              <span className="font-mono tabular-nums text-slate-300 font-semibold">{fl}</span>
            </div>
          )}
          {bestSector && bestSector.sector.changePct > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-slate-500 uppercase tracking-wider">Best</span>
              <span className="text-slate-200">{bestSector.sector.label}</span>
              <span className="font-mono tabular-nums text-emerald-300 font-semibold">{fP(bestSector.sector.changePct)}</span>
            </div>
          )}
          {worstSector && worstSector.sector.changePct < 0 && (
            <div className="flex items-center gap-2">
              <span className="text-slate-500 uppercase tracking-wider">Worst</span>
              <span className="text-slate-200">{worstSector.sector.label}</span>
              <span className="font-mono tabular-nums text-rose-300 font-semibold">{fP(worstSector.sector.changePct)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Country blocks */}
      <div className="space-y-5">
        {markets.map(function(m) {
          var secs = sectorsByMarket[m.market] || [];
          if (!secs.length) return null;
          var href = '/markets/' + (MARKET_SHORT[m.market] || m.market.toLowerCase());
          return (
            <div key={m.market} className="gcc-card p-4">
              <div className="flex items-center justify-between mb-3">
                <a href={href} className="flex items-center gap-3 no-underline" style={{textDecoration:'none'}}>
                  <span style={{fontSize:22}}>{m.flag}</span>
                  <div>
                    <div className="text-[13px] font-semibold text-slate-100">{m.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      {m.short} · {secs.length} sectors
                    </div>
                  </div>
                </a>
                <div className="text-right">
                  <div className={'font-mono tabular-nums text-[13px] font-semibold ' + cc(m.changePct)}>{fP(m.changePct)}</div>
                  <div className="text-[10px] text-slate-500 font-mono tabular-nums">{fN(m.value)}</div>
                </div>
              </div>
              <div className="grid gap-2" style={{gridTemplateColumns:'repeat(auto-fill, minmax(135px, 1fr))'}}>
                {secs.map(function(s) {
                  var col = sectorTileColor(s.changePct);
                  var pctStr = s.changePct == null ? '—' : (s.changePct >= 0 ? '+' : '') + s.changePct.toFixed(2) + '%';
                  return (
                    <div key={s.code} className="rounded-md px-2.5 py-2"
                         style={{background:col.bg, border:'1px solid '+col.border, minHeight:64}}
                         title={s.label + ' · ' + s.code + ' · ' + pctStr}>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider truncate" style={{letterSpacing:'0.04em'}}>{s.code}</div>
                      <div className="text-[11px] text-slate-200 leading-tight mt-0.5 line-clamp-2" style={{overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>
                        {s.label}
                      </div>
                      <div className="mt-1.5 font-mono tabular-nums text-[12px] font-semibold" style={{color:col.text}}>{pctStr}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </React.Fragment>
  );
}

// ── Heatmap component ────────────────────────────────────────
function Heatmap({ markets }) {
  if (!markets || markets.length === 0) return null;

  // Calculate sizes by turnover
  var maxTurnover = Math.max(...markets.map(function(m){ return m.turnover || 0; }));

  return (
    <div className="heatmap-grid">
      {markets.map(function(m) {
        var up = (m.changePct||0) >= 0;
        var bgColor = up ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)';
        var borderColor = up ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)';
        var pct = m.turnover && maxTurnover ? (m.turnover / maxTurnover) * 100 : 50;
        var href = '/markets/' + (MARKET_SHORT[m.market]||m.market.toLowerCase());

        return (
          <a href={href} key={m.market}
            className="heatmap-tile"
            style={{background:bgColor,borderColor:borderColor,minHeight:140+Math.max(0,pct-30)}}>
            <div>
              <div style={{fontSize:28,marginBottom:8}}>{m.flag}</div>
              <div className="text-sm font-bold text-white mb-1">{m.short}</div>
              <div className="text-xs text-slate-500 mb-3">{m.label}</div>
            </div>
            <div>
              <div className="font-mono text-lg font-semibold" style={{color:up?'#4ade80':'#f87171'}}>
                {fP(m.changePct)}
              </div>
              <div className="font-mono text-xs text-slate-500 mt-1">
                {fN(m.value)}
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
}

// ── Watchlist component ──────────────────────────────────────
function Watchlist({ allStocks, t }) {
  t = t || I18N.en;
  var [watchlist, setWatchlist] = useState(function() {
    var w = localStorage.getItem('marsad_watchlist');
    return w ? JSON.parse(w) : [];
  });

  function removeFromWatchlist(code, market) {
    var updated = watchlist.filter(function(item) {
      if (typeof item === 'string') return item !== code;
      return !(item.code === code && item.market === market);
    });
    setWatchlist(updated);
    localStorage.setItem('marsad_watchlist', JSON.stringify(updated));
  }

  if (watchlist.length === 0) {
    return (
      <div className="gcc-card p-8 text-center">
        <div className="text-3xl mb-3">⭐</div>
        <div className="text-slate-300 font-medium mb-1">{t.watchlistEmpty}</div>
        <div className="text-sm text-slate-500">{t.watchlistHint}</div>
      </div>
    );
  }

  // Build watchlist data from allStocks
  // Watchlist items are {market, code, name} objects (from stock page)
  var watchlistData = [];
  for (var i = 0; i < watchlist.length; i++) {
    var item = watchlist[i];
    var code = typeof item === 'string' ? item : item.code;
    var mkt = typeof item === 'string' ? '' : (item.market || '');
    var stock = allStocks ? allStocks.find(function(s) {
      return (s.c || '') === code;
    }) : null;
    watchlistData.push({
      ticker: code,
      name: stock ? (stock.n || code) : (item.name || code),
      flag: stock ? (stock.f || '') : '',
      market: stock ? (stock.l || stock.m || '') : mkt,
      mktCode: mkt,
      price: stock ? stock.p : null,
      change: stock ? stock.ch : null,
      changePct: stock ? stock.cp : null
    });
  }

  return (
    <div className="gcc-card overflow-hidden">
      <div className="p-4 border-b border-white/5">
        <div className="text-sm font-semibold text-white">★ Watchlist ({watchlistData.length})</div>
      </div>
      <table className="watchlist-table">
        <thead>
          <tr style={{fontSize:11,color:'#64748b',borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
            <td style={{padding:'10px 16px'}}>TICKER</td>
            <td style={{padding:'10px 16px',textAlign:'right'}}>PRICE</td>
            <td style={{padding:'10px 16px',textAlign:'right'}}>CHANGE</td>
            <td style={{padding:'10px 16px',textAlign:'center',width:40}}>REMOVE</td>
          </tr>
        </thead>
        <tbody>
          {watchlistData.map(function(w) {
            var up = (w.changePct||0) >= 0;
            var color = up ? '#4ade80' : '#f87171';
            return (
              <tr key={w.ticker}>
                <td style={{color:'#e2e8f0'}}>
                  <div className="font-semibold font-mono">{w.ticker}</div>
                  <div style={{fontSize:11,color:'#64748b',marginTop:2}}>{w.name}</div>
                </td>
                <td style={{textAlign:'right',color:'#e2e8f0',fontFamily:'IBM Plex Mono',fontWeight:500}}>
                  {fN(w.price)}
                </td>
                <td style={{textAlign:'right',color:color,fontFamily:'IBM Plex Mono',fontWeight:600}}>
                  {fP(w.changePct)}
                </td>
                <td style={{textAlign:'center'}}>
                  <button onClick={function(e){ e.preventDefault(); removeFromWatchlist(w.ticker, w.mktCode); }}
                    className="text-slate-500 hover:text-rose-400 transition-colors text-xs"
                    style={{background:'none',border:'none',cursor:'pointer'}}>
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── AI Daily Brief ───────────────────────────────────────────
// Claude-generated 3-bullet summary of the day's GCC market moves.
// Fetched from /.netlify/functions/markets-brief (cached 1hr server-side).
// Falls back silently if the endpoint is unavailable (keeps the page
// clean rather than showing an error for what's really a "nice to have").
function DailyBrief({ lang }) {
  var [brief, setBrief] = React.useState(null);
  var [loading, setLoading] = React.useState(true);

  React.useEffect(function() {
    var cancelled = false;
    setLoading(true);
    fetch('/.netlify/functions/markets-brief')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (cancelled) return;
        if (d && Array.isArray(d.en) && Array.isArray(d.ar)) setBrief(d);
        setLoading(false);
      })
      .catch(function() { if (!cancelled) setLoading(false); });
    return function() { cancelled = true; };
  }, []);

  if (loading || !brief) return null;
  var bullets = lang === 'ar' ? brief.ar : brief.en;
  var label = lang === 'ar' ? '✨ موجز اليوم' : '✨ TODAY\u2019S BRIEF';
  return (
    <div className="gcc-card p-4 mb-6" style={{
      background:'linear-gradient(135deg,rgba(0,240,255,0.04),rgba(181,255,28,0.02))',
      borderColor:'rgba(0,240,255,0.15)'
    }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{color:'#06b6d4'}}>{label}</span>
        {brief.ts && (
          <span className="text-[9px] text-slate-600 font-mono">{new Date(brief.ts).toLocaleTimeString(lang==='ar'?'ar-KW':'en-US',{hour:'2-digit',minute:'2-digit'})}</span>
        )}
      </div>
      <ul className="space-y-1.5 text-[13px] text-slate-200 leading-snug"
          style={{fontFamily: lang==='ar' ? "'IBM Plex Sans Arabic','IBM Plex Sans',sans-serif" : undefined}}>
        {bullets.map(function(b, i) {
          return (
            <li key={i} className="flex gap-2" style={lang==='ar'?{flexDirection:'row-reverse'}:undefined}>
              <span className="text-cyan-400/70 font-bold shrink-0">{i+1}.</span>
              <span>{b}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Cross-market leaderboards ────────────────────────────────
// Three horizontal scrollable rails showing the day's hottest
// stocks across ALL GCC markets. Data comes from hub.leaderboards
// (computed server-side, aggregated from each market's movers).
function Leaderboards({ leaderboards, t }) {
  t = t || I18N.en;
  if (!leaderboards) return null;

  function Rail({ title, emoji, items, metric }) {
    if (!items || items.length === 0) return null;
    return (
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span style={{fontSize:16}}>{emoji}</span>
          <h3 className="text-[11px] font-bold tracking-wider uppercase text-slate-400">{title}</h3>
          <span className="text-[10px] text-slate-600 font-mono">({items.length})</span>
        </div>
        <div style={{display:'flex',gap:8,overflowX:'auto',paddingBottom:6,scrollBehavior:'smooth'}}>
          {items.map(function(s, i) {
            var href = '/markets/' + MARKET_SHORT[s.market] + '/' + encodeURIComponent(s.code);
            var up = (s.changePct||0) >= 0;
            var color = up ? '#4ade80' : '#f87171';
            return (
              <a key={i} href={href}
                 className="gcc-card"
                 style={{
                   textDecoration:'none',color:'inherit',padding:'10px 12px',
                   minWidth:150,flex:'0 0 auto',display:'flex',flexDirection:'column',gap:4
                 }}>
                <div className="flex items-center gap-1.5 text-[9px] text-slate-500">
                  <span style={{fontSize:12}}>{s.flag}</span>
                  <span className="uppercase tracking-wider font-mono">{s.short}</span>
                </div>
                <div className="text-[13px] font-bold text-white font-mono truncate" title={s.name}>{s.code}</div>
                {s.value != null && (
                  <div className="text-[11px] text-slate-400 font-mono tabular-nums">{fN(s.value)}</div>
                )}
                <div className="text-[12px] font-bold font-mono" style={{color:color}}>
                  {metric === 'turnover'
                    ? fB(s.turnover) + ' tov'
                    : (s.changePct >= 0 ? '+' : '') + fP(s.changePct).replace('+','')}
                </div>
              </a>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-2">
      <Rail title={t.lang==='ar' ? 'الأكثر ارتفاعاً' : 'Top Gainers'} emoji="🔥" items={leaderboards.gainers} metric="changePct"/>
      <Rail title={t.lang==='ar' ? 'الأكثر انخفاضاً' : 'Top Losers'} emoji="🧊" items={leaderboards.losers} metric="changePct"/>
      <Rail title={t.lang==='ar' ? 'الأكثر تداولاً' : 'Most Active'} emoji="💎" items={leaderboards.active} metric="turnover"/>
    </div>
  );
}

// ── News Feed (homepage) ─────────────────────────────────────
// Renders the hub headlines in a clean two-column layout.
// Each headline shows: market flag, time-ago, title, source link.
function NewsFeed({ headlines, t }) {
  t = t || I18N.en;
  if (!headlines || headlines.length === 0) {
    return (
      <div className="gcc-card p-8 text-center">
        <div className="text-3xl mb-3">📰</div>
        <div className="text-slate-300 font-medium mb-1">{t.newsEmpty}</div>
      </div>
    );
  }

  function timeAgo(iso) {
    if (!iso) return '';
    var d = new Date(iso.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return iso;
    var diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return Math.floor(diff/60) + 'm ago';
    if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
    return Math.floor(diff/86400) + 'd ago';
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {headlines.map(function(n, i) {
        return (
          <a key={i} href={n.url} target="_blank" rel="noopener noreferrer"
             className="gcc-card p-4 block hover:border-cyan-400/30 transition-colors"
             style={{textDecoration:'none',color:'inherit'}}>
            <div className="flex items-center gap-2 mb-2 text-[10px] text-slate-500">
              {n.flag && <span style={{fontSize:14}}>{n.flag}</span>}
              {n.market && <span className="uppercase tracking-wider font-mono text-slate-400">{n.market}</span>}
              <span className="text-slate-700">·</span>
              <span>{timeAgo(n.publishedAt)}</span>
            </div>
            <div className="text-[13px] font-medium text-slate-100 leading-snug" style={{display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical',overflow:'hidden'}}>
              {n.title}
            </div>
          </a>
        );
      })}
    </div>
  );
}

// ── Disclosures feed (bilingual) ─────────────────────────────
// Regulatory disclosures aggregated from all 7 GCC exchanges.
// Title + teaser rendered in the current language (Arabic or
// English) with a small per-item toggle to flip to the other
// language. Source: /.netlify/functions/markets-disclosures
// (refreshed daily by a scheduled task).
// Sentiment + tag badge styling. Colors are tuned to the dashboard's
// existing palette — emerald/rose for sentiment, slate pills for the tag
// so the taxonomy doesn't compete with the sentiment signal.
var SENTIMENT_STYLE = {
  positive: { bg: 'rgba(74,222,128,0.12)',  border: 'rgba(74,222,128,0.35)',  text: '#86efac', label: '▲' },
  negative: { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.35)', text: '#fca5a5', label: '▼' },
  neutral:  { bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.25)', text: '#cbd5e1', label: '•' },
};
var TAG_LABEL_EN = {
  'earnings':'Earnings','dividend':'Dividend','agm':'AGM','board':'Board Meeting',
  'bonus-shares':'Bonus Shares','buyback':'Buyback','capital-increase':'Capital Increase',
  'ipo':'IPO','delisting':'Delisting','acquisition':'Acquisition','contract':'Contract',
  'credit-rating':'Credit Rating','litigation':'Litigation','regulatory':'Regulatory',
  'material-event':'Material Event','other':'Other',
};
var TAG_LABEL_AR = {
  'earnings':'أرباح','dividend':'توزيعات','agm':'جمعية عامة','board':'اجتماع مجلس',
  'bonus-shares':'أسهم منحة','buyback':'إعادة شراء','capital-increase':'زيادة رأس المال',
  'ipo':'اكتتاب','delisting':'شطب','acquisition':'استحواذ','contract':'عقد',
  'credit-rating':'تصنيف ائتماني','litigation':'قضائي','regulatory':'تنظيمي',
  'material-event':'حدث جوهري','other':'أخرى',
};

function DisclosuresFeed({ lang, t }) {
  t = t || I18N.en;
  var [data, setData] = React.useState(null);
  var [loading, setLoading] = React.useState(true);
  var [flipped, setFlipped] = React.useState({}); // { [id]: true } → show other lang
  var [filter, setFilter] = React.useState('all'); // 'all' | 'positive' | 'negative' | 'neutral'

  React.useEffect(function() {
    var cancelled = false;
    setLoading(true);
    fetch('/.netlify/functions/markets-disclosures')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(function() { if (!cancelled) setLoading(false); });
    return function() { cancelled = true; };
  }, []);

  function timeAgo(iso) {
    if (!iso) return '';
    var d = new Date(iso.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return iso;
    var diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return lang==='ar' ? 'الآن' : 'just now';
    if (diff < 3600) return Math.floor(diff/60) + (lang==='ar' ? ' د' : 'm ago');
    if (diff < 86400) return Math.floor(diff/3600) + (lang==='ar' ? ' س' : 'h ago');
    return Math.floor(diff/86400) + (lang==='ar' ? ' ي' : 'd ago');
  }

  if (loading) return <div className="text-slate-500 text-sm">{t.loading}</div>;
  if (!data || !data.disclosures || data.disclosures.length === 0) {
    return (
      <div className="gcc-card p-8 text-center">
        <div className="text-3xl mb-3">📋</div>
        <div className="text-slate-300 font-medium">{t.disclosuresEmpty}</div>
      </div>
    );
  }

  // Sentiment counts for the filter header — only computed when classifier
  // data is present (older cached responses predate sentiment/tag fields).
  var allD = data.disclosures;
  var hasClass = allD.some(function(d){ return d.sentiment; });
  var counts = { all: allD.length, positive: 0, negative: 0, neutral: 0 };
  allD.forEach(function(d){ if (d.sentiment && counts[d.sentiment] != null) counts[d.sentiment]++; });
  var filtered = filter === 'all' ? allD : allD.filter(function(d){ return d.sentiment === filter; });

  var tagLabels = lang === 'ar' ? TAG_LABEL_AR : TAG_LABEL_EN;

  return (
    <React.Fragment>
      {hasClass && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" style={{direction:'ltr'}}>
          {['all','positive','negative','neutral'].map(function(k) {
            var active = filter === k;
            var pill = SENTIMENT_STYLE[k] || { bg:'rgba(148,163,184,0.08)', border:'rgba(148,163,184,0.25)', text:'#cbd5e1', label:'' };
            return (
              <button key={k} onClick={function(){ setFilter(k); }}
                      className={'px-2.5 py-1 text-[11px] rounded-md transition-colors ' + (active ? 'font-semibold' : 'hover:border-slate-500')}
                      style={{
                        background: active ? pill.bg : 'transparent',
                        border: '1px solid ' + (active ? pill.border : 'rgba(148,163,184,0.22)'),
                        color: active ? pill.text : '#94a3b8',
                        cursor: 'pointer',
                      }}>
                {k === 'all' ? (lang==='ar'?'الكل':'All') : (pill.label + ' ' + (k === 'positive' ? (lang==='ar'?'إيجابي':'Positive')
                                             : k === 'negative' ? (lang==='ar'?'سلبي':'Negative')
                                             : (lang==='ar'?'محايد':'Neutral')))}
                <span className="ml-1.5 opacity-70 font-mono tabular-nums">{counts[k]}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="space-y-2">
      {filtered.map(function(d) {
        var flip = flipped[d.id];
        var showAr = (lang === 'ar') !== !!flip;
        var title  = showAr && d.titleAr  ? d.titleAr  : d.title;
        var teaser = showAr && d.teaserAr ? d.teaserAr : d.teaser;
        var hasOtherLang = showAr ? !!d.title : !!d.titleAr;
        var sent = d.sentiment && SENTIMENT_STYLE[d.sentiment] ? SENTIMENT_STYLE[d.sentiment] : null;
        return (
          <div key={d.id} className="gcc-card p-3 hover:border-cyan-400/30 transition-colors"
               style={{
                 direction: showAr ? 'rtl' : 'ltr',
                 borderLeft: sent ? '3px solid ' + sent.border : undefined,
               }}>
            <div className="flex items-center gap-2 mb-1.5 text-[10px] text-slate-500 flex-wrap"
                 style={{direction: 'ltr'}}>
              {d.flag && <span style={{fontSize:13}}>{d.flag}</span>}
              <span className="uppercase tracking-wider font-mono text-slate-400">{d.short || d.market}</span>
              {sent && (
                <span style={{
                  padding:'1px 6px',
                  borderRadius:3,
                  background: sent.bg,
                  border: '1px solid ' + sent.border,
                  color: sent.text,
                  fontSize:9,
                  fontWeight:600,
                  letterSpacing:'0.05em',
                }}>
                  {sent.label} {d.sentiment.toUpperCase()}
                </span>
              )}
              {d.tag && d.tag !== 'other' && (
                <span style={{
                  padding:'1px 6px',
                  borderRadius:3,
                  background:'rgba(148,163,184,0.08)',
                  border:'1px solid rgba(148,163,184,0.22)',
                  color:'#cbd5e1',
                  fontSize:9,
                  letterSpacing:'0.04em',
                }}>
                  {tagLabels[d.tag] || d.tag}
                </span>
              )}
              <span className="text-slate-700">·</span>
              <span>{timeAgo(d.publishedAt)}</span>
              {d.source && <>
                <span className="text-slate-700">·</span>
                <span className="truncate max-w-[240px]">{d.source}</span>
              </>}
            </div>
            <a href={d.url || '#'} target="_blank" rel="noopener noreferrer"
               className="block text-[13px] font-medium text-slate-100 leading-snug hover:text-cyan-300 transition-colors"
               style={{textDecoration:'none', fontFamily: showAr ? "'IBM Plex Sans Arabic','IBM Plex Sans',sans-serif" : undefined}}>
              {title}
            </a>
            {teaser && teaser.length > 0 && !teaser.startsWith('http') && (
              <div className="text-[11px] text-slate-500 mt-1 leading-snug"
                   style={{fontFamily: showAr ? "'IBM Plex Sans Arabic','IBM Plex Sans',sans-serif" : undefined}}>
                {teaser.slice(0, 200)}{teaser.length > 200 ? '…' : ''}
              </div>
            )}
            {hasOtherLang && (
              <button onClick={function() { setFlipped(Object.assign({}, flipped, { [d.id]: !flip })); }}
                      className="mt-1.5 text-[10px] text-cyan-400/70 hover:text-cyan-300 transition-colors"
                      style={{background:'none',border:'none',cursor:'pointer',padding:0,direction:'ltr'}}>
                {showAr ? t.showInEnglish : t.showInArabic} →
              </button>
            )}
          </div>
        );
      })}
      </div>
    </React.Fragment>
  );
}

// ── Calendar (corporate actions + shareholder meetings) ──────
// Classify an event by keyword match on title+agenda. Runs client-side —
// no backend cost. We treat earnings/results/quarterly language as
// "earnings", AGM/OGM/EGM as "agm", dividend/distribution as "dividend",
// and everything else as "other".
var EARNINGS_RX = /(earnings|financial results|financial statements|quarterly results|annual results|interim results|q1|q2|q3|q4|fy\d|half-year|full-year|fiscal)/i;
var DIVIDEND_RX = /(dividend|distribution|cash payout|بتوزيع|توزيعات)/i;
var AGM_RX      = /(annual general|ordinary general|extraordinary general|\bagm\b|\bogm\b|\begm\b|الجمعية العامة)/i;
var BOARD_RX    = /(board meeting|board of directors|اجتماع مجلس|مجلس الإدارة)/i;

function classifyEvent(e) {
  var blob = ((e.title || '') + ' ' + (e.agenda || '')).trim();
  if (EARNINGS_RX.test(blob)) return 'earnings';
  if (DIVIDEND_RX.test(blob)) return 'dividend';
  if (AGM_RX.test(blob))      return 'agm';
  if (BOARD_RX.test(blob))    return 'board';
  return 'other';
}

var CAL_FILTER_LABEL_EN = { all:'All', earnings:'Earnings', dividend:'Dividend', agm:'AGM', board:'Board', other:'Other' };
var CAL_FILTER_LABEL_AR = { all:'الكل', earnings:'الأرباح', dividend:'توزيعات', agm:'الجمعية', board:'المجلس', other:'أخرى' };

function Calendar({ events, t }) {
  t = t || I18N.en;
  var [filter, setFilter] = useState('all');
  if (!events || events.length === 0) {
    return (
      <div className="gcc-card p-8 text-center">
        <div className="text-3xl mb-3">📅</div>
        <div className="text-slate-300 font-medium mb-1">{t.calendarEmpty}</div>
      </div>
    );
  }

  // Tag every event, count by category, then filter.
  var tagged = events.map(function(e){ return Object.assign({}, e, { _kind: classifyEvent(e) }); });
  var counts = { all: tagged.length, earnings:0, dividend:0, agm:0, board:0, other:0 };
  tagged.forEach(function(e){ counts[e._kind]++; });
  var filtered = filter === 'all' ? tagged : tagged.filter(function(e){ return e._kind === filter; });

  var filterLabels = t.lang === 'ar' ? CAL_FILTER_LABEL_AR : CAL_FILTER_LABEL_EN;

  // Relative-time helper so the date column shows "Today" / "3 days" /
  // "next week" alongside the ISO date — helps at-a-glance scanning.
  function relDate(iso) {
    if (!iso) return '';
    var parts = iso.split('-');
    if (parts.length < 3) return '';
    var d = new Date(Date.UTC(+parts[0], +parts[1]-1, +parts[2]));
    var today = new Date();
    var todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    var days = Math.round((d - todayUTC) / (1000*60*60*24));
    if (t.lang === 'ar') {
      if (days < 0)  return 'فات';
      if (days === 0) return 'اليوم';
      if (days === 1) return 'غدًا';
      if (days < 7)   return 'خلال ' + days + ' أيام';
      if (days < 14)  return 'الأسبوع القادم';
      if (days < 30)  return 'خلال ' + Math.ceil(days/7) + ' أسابيع';
      return 'خلال ' + Math.ceil(days/30) + ' شهر';
    } else {
      if (days < 0)  return 'past';
      if (days === 0) return 'today';
      if (days === 1) return 'tomorrow';
      if (days < 7)   return 'in ' + days + 'd';
      if (days < 14)  return 'next week';
      if (days < 30)  return 'in ' + Math.ceil(days/7) + 'w';
      return 'in ' + Math.ceil(days/30) + 'mo';
    }
  }

  function kindBadge(kind) {
    var map = {
      earnings: { bg:'rgba(74,222,128,0.12)',  border:'rgba(74,222,128,0.32)',  text:'#86efac', label: filterLabels.earnings, icon:'📊' },
      dividend: { bg:'rgba(6,182,212,0.10)',   border:'rgba(6,182,212,0.32)',   text:'#67e8f9', label: filterLabels.dividend, icon:'💰' },
      agm:      { bg:'rgba(168,85,247,0.10)',  border:'rgba(168,85,247,0.30)',  text:'#d8b4fe', label: filterLabels.agm,      icon:'🏛️' },
      board:    { bg:'rgba(251,146,60,0.10)',  border:'rgba(251,146,60,0.30)',  text:'#fdba74', label: filterLabels.board,    icon:'👥' },
      other:    { bg:'rgba(148,163,184,0.08)', border:'rgba(148,163,184,0.22)', text:'#cbd5e1', label: filterLabels.other,    icon:'•'  },
    };
    return map[kind] || map.other;
  }

  return (
    <React.Fragment>
      <div className="flex items-center gap-2 mb-3 flex-wrap" style={{direction:'ltr'}}>
        {['all','earnings','dividend','agm','board','other'].map(function(k){
          var active = filter === k;
          var badge = k === 'all' ? { bg:'rgba(6,182,212,0.10)', border:'rgba(6,182,212,0.35)', text:'#67e8f9', icon:'' } : kindBadge(k);
          return (
            <button key={k} onClick={function(){ setFilter(k); }}
                    className="px-2.5 py-1 text-[11px] rounded-md transition-colors"
                    style={{
                      background: active ? badge.bg : 'transparent',
                      border: '1px solid ' + (active ? badge.border : 'rgba(148,163,184,0.22)'),
                      color: active ? badge.text : '#94a3b8',
                      cursor:'pointer',
                      fontWeight: active ? 600 : 400,
                    }}>
              {badge.icon && <span style={{marginRight:4}}>{badge.icon}</span>}
              {filterLabels[k]} <span className="ml-1 opacity-70 font-mono tabular-nums">{counts[k]}</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="gcc-card p-6 text-center text-slate-500 text-sm">
          {t.lang === 'ar' ? 'لا توجد أحداث في هذه الفئة.' : 'No events in this category.'}
        </div>
      ) : (
        <div className="gcc-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr style={{fontSize:10,color:'#64748b',borderBottom:'1px solid rgba(255,255,255,0.05)',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                <td style={{padding:'10px 16px',width:'20%'}}>{t.date}</td>
                <td style={{padding:'10px 16px',width:'13%'}}>{t.market}</td>
                <td style={{padding:'10px 16px',width:'18%'}}>{t.company}</td>
                <td style={{padding:'10px 16px',width:'18%'}}>{t.event}</td>
                <td style={{padding:'10px 16px'}}>{t.agenda}</td>
              </tr>
            </thead>
            <tbody>
              {filtered.map(function(e, i){
                var badge = kindBadge(e._kind);
                var rel = relDate(e.from);
                return (
                  <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.03)',fontSize:12}}>
                    <td style={{padding:'12px 16px',fontFamily:'monospace',fontSize:11}}>
                      <div className="text-slate-300">{e.from}{e.to ? ' → ' + e.to : ''}</div>
                      {rel && <div className="text-[10px] text-slate-500 mt-0.5" style={{fontFamily:'inherit'}}>{rel}</div>}
                    </td>
                    <td style={{padding:'12px 16px'}}>
                      <span style={{fontSize:14,marginRight:6}}>{e.flag||''}</span>
                      <span className="uppercase tracking-wider text-slate-400 font-mono text-[10px]">{e.market||''}</span>
                    </td>
                    <td style={{padding:'12px 16px'}}>
                      <a href={e.hostUrl||'#'} target="_blank" rel="noopener noreferrer"
                         className="text-cyan-300 hover:text-cyan-200 font-semibold font-mono text-[11px]"
                         style={{textDecoration:'none'}}>
                        {e.host||'—'}
                      </a>
                    </td>
                    <td style={{padding:'12px 16px'}}>
                      <span style={{
                        display:'inline-block',
                        padding:'2px 7px',
                        borderRadius:4,
                        background: badge.bg,
                        border:'1px solid '+badge.border,
                        color: badge.text,
                        fontSize:10,
                        fontWeight:600,
                        letterSpacing:'0.03em',
                      }}>{badge.icon} {badge.label}</span>
                      {e.title && <div className="text-[11px] text-slate-300 mt-1 leading-snug">{e.title}</div>}
                    </td>
                    <td style={{padding:'12px 16px',color:'#94a3b8',fontSize:11,whiteSpace:'pre-wrap'}}>
                      {(e.agenda||'').trim() || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </React.Fragment>
  );
}

// ── Web Push VAPID + helpers ─────────────────────────────────
// This mirrors index.html's bell plumbing (around line 6481) so the
// markets page can POST to /.netlify/functions/markets-alerts with a
// full push subscription payload. We don't reuse the topic-bell from
// the main site — that one is for a single Hormuz alert. Markets needs
// multi-rule per-ticker subscriptions.
var VAPID_PUB_B64 = 'BGIexrxE7250rsctR56VK4WvM8YVDk7Gk4QIs-6U47UrHDBgIXNoY5Jtjdk4TiM7BAdV0wDlAW4kSPDGmwY-Q00';
function urlB64ToUint8(b64) {
  var pad = '='.repeat((4 - b64.length % 4) % 4);
  var raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  var arr = new Uint8Array(raw.length);
  for (var i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}
function bufToB64Url(buf) {
  var bytes = new Uint8Array(buf);
  var bin = '';
  for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Full GCC market catalog, ordered the way the hub returns them. Used
// for the add-alert form's market dropdown.
var ALERT_MARKETS = [
  { code: 'BK',   label: 'Kuwait (Boursa Kuwait)' },
  { code: 'TDWL', label: 'Saudi (Tadawul)' },
  { code: 'DFM',  label: 'Dubai (DFM)' },
  { code: 'ADX',  label: 'Abu Dhabi (ADX)' },
  { code: 'QE',   label: 'Qatar (QSE)' },
  { code: 'BB',   label: 'Bahrain (BHB)' },
  { code: 'MSM',  label: 'Oman (MSX)' },
];

// Acquire (or subscribe, if user hasn't yet) a valid push subscription.
// Returns null on failure (no SW, permission denied, browser blocked).
// The heavy lifting is identical to index.html's flow; kept inline here
// so the bundle doesn't need a shared helper module.
async function ensurePushSubscription() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  var reg;
  try { reg = await navigator.serviceWorker.ready; } catch (e) { return null; }
  if (!reg) return null;
  var existing = await reg.pushManager.getSubscription();
  if (existing) return existing;
  // No subscription yet — request permission and subscribe.
  if (Notification.permission === 'denied') return null;
  if (Notification.permission !== 'granted') {
    var perm = await Notification.requestPermission();
    if (perm !== 'granted') return null;
  }
  try {
    var sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8(VAPID_PUB_B64),
    });
    // Also register with the push-subscribe blob so the main notify
    // pipeline has the subscription on file even if no topic is opted
    // into yet (the markets-alerts-check function posts directly using
    // the keys stored in the alerts blob, so this is cosmetic but nice).
    try {
      await fetch('/.netlify/functions/push-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: sub.endpoint,
          keys: {
            p256dh: bufToB64Url(sub.getKey('p256dh')),
            auth:   bufToB64Url(sub.getKey('auth')),
          },
        }),
      });
    } catch (e) {}
    return sub;
  } catch (e) {
    console.warn('[marsad-markets] subscribe failed', e);
    return null;
  }
}

// ── Alerts Modal ─────────────────────────────────────────────
// Bell-icon dialog for creating, listing, and removing per-ticker
// price alerts. Uses /.netlify/functions/markets-alerts for storage.
// The backend stores by push endpoint, so we key everything off the
// user's current push subscription. If the browser doesn't support
// push or the user declines, we show a friendly fallback message.
function AlertsModal({ open, onClose, lang, t }) {
  var [sub, setSub] = useState(undefined); // undefined=loading, null=unavailable, obj=ready
  var [alerts, setAlertsList] = useState(null);
  var [loadErr, setLoadErr] = useState(null);
  var [saving, setSaving] = useState(false);
  var [form, setForm] = useState({ market: 'BK', code: '', direction: 'above', threshold: '' });
  var [formErr, setFormErr] = useState(null);

  // Once the modal opens, resolve the push subscription + list existing
  // alerts. We do this lazily so a user who never opens the bell doesn't
  // get pinged for notification permission.
  useEffect(function() {
    if (!open) return;
    var cancelled = false;
    setLoadErr(null);
    setAlertsList(null);
    ensurePushSubscription().then(function(s) {
      if (cancelled) return;
      setSub(s || null);
      if (!s) return;
      fetch('/.netlify/functions/markets-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ op: 'list', endpoint: s.endpoint }),
      })
        .then(function(r) { return r.json().catch(function() { return {}; }); })
        .then(function(j) {
          if (cancelled) return;
          if (j && j.ok && Array.isArray(j.alerts)) setAlertsList(j.alerts);
          else setLoadErr((j && j.error) || 'Could not load alerts');
        })
        .catch(function(e) { if (!cancelled) setLoadErr(String(e.message || e)); });
    });
    return function() { cancelled = true; };
  }, [open]);

  async function addAlert() {
    setFormErr(null);
    var code = String(form.code || '').trim().toUpperCase();
    var thr = parseFloat(form.threshold);
    if (!code) { setFormErr(lang==='ar' ? 'الرجاء إدخال رمز الشركة' : 'Please enter a ticker code'); return; }
    if (!isFinite(thr) || thr <= 0) { setFormErr(lang==='ar' ? 'السعر المستهدف غير صالح' : 'Invalid target price'); return; }
    if (!sub) { setFormErr(lang==='ar' ? 'الإشعارات غير متاحة' : 'Notifications unavailable'); return; }
    setSaving(true);
    try {
      var res = await fetch('/.netlify/functions/markets-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          op: 'add',
          subscription: {
            endpoint: sub.endpoint,
            keys: {
              p256dh: bufToB64Url(sub.getKey('p256dh')),
              auth:   bufToB64Url(sub.getKey('auth')),
            },
            expirationTime: sub.expirationTime || null,
          },
          alert: { market: form.market, code: code, direction: form.direction, threshold: thr },
        }),
      });
      var j = await res.json().catch(function() { return {}; });
      if (!res.ok || !j.ok) throw new Error(j.error || ('HTTP ' + res.status));
      setAlertsList((alerts || []).concat([j.alert]));
      setForm({ market: form.market, code: '', direction: 'above', threshold: '' });
    } catch (e) {
      setFormErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function removeAlert(id) {
    if (!sub) return;
    var prev = alerts || [];
    setAlertsList(prev.filter(function(a) { return a.id !== id; }));
    try {
      await fetch('/.netlify/functions/markets-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ op: 'remove', endpoint: sub.endpoint, id: id }),
      });
    } catch (e) {
      // Re-add on failure so the user isn't surprised.
      setAlertsList(prev);
    }
  }

  if (!open) return null;

  var isAr = lang === 'ar';
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/70 p-4 overflow-y-auto"
         onClick={onClose}
         style={isAr ? {fontFamily:"'IBM Plex Sans Arabic','IBM Plex Sans',sans-serif"} : undefined}>
      <div className="gcc-card max-w-lg w-full p-5 sm:p-6"
           onClick={function(e){ e.stopPropagation(); }}
           dir={isAr ? 'rtl' : 'ltr'}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-lg">🔔</span>
            <h2 className="text-base font-semibold text-white">
              {isAr ? 'تنبيهات الأسعار' : 'Price Alerts'}
            </h2>
          </div>
          <button onClick={onClose}
                  className="text-slate-400 hover:text-white w-8 h-8 flex items-center justify-center rounded hover:bg-white/5"
                  aria-label={isAr ? 'إغلاق' : 'Close'}>
            ✕
          </button>
        </div>

        {/* Description */}
        <div className="text-[11px] text-slate-500 mb-4 leading-snug">
          {isAr
            ? 'يرسل الخادم إشعارًا عند تجاوز السعر الحد المحدد. يتم التحقق كل 5 دقائق أثناء ساعات السوق.'
            : 'Server pushes a notification when the price crosses your threshold. Checked every 5 minutes during market hours.'}
        </div>

        {sub === undefined && (
          <div className="text-slate-500 text-sm py-4">{isAr ? 'جارٍ التحميل…' : 'Loading…'}</div>
        )}

        {sub === null && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[12px] text-amber-300/90 leading-snug">
            {isAr
              ? 'الإشعارات غير متاحة في هذا المتصفح، أو رفضت صلاحية الإشعارات. فعّل الإشعارات من إعدادات المتصفح ثم أعد المحاولة.'
              : 'Notifications are unavailable in this browser or permission was denied. Enable notifications in your browser settings and try again.'}
          </div>
        )}

        {sub && (
          <React.Fragment>
            {/* Existing alerts list */}
            <div className="mb-4">
              <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">
                {isAr ? 'التنبيهات النشطة' : 'Active Alerts'}
                {alerts && <span className="ml-2 text-slate-600">({alerts.length})</span>}
              </div>
              {loadErr && (
                <div className="text-[12px] text-rose-400">{loadErr}</div>
              )}
              {alerts && alerts.length === 0 && !loadErr && (
                <div className="text-[12px] text-slate-500 italic py-2">
                  {isAr ? 'لا توجد تنبيهات بعد.' : 'No alerts yet.'}
                </div>
              )}
              {alerts && alerts.length > 0 && (
                <ul className="flex flex-col gap-1.5">
                  {alerts.map(function(a) {
                    return (
                      <li key={a.id}
                          className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-white/10 bg-white/[0.02]">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-[12px]">
                            <span className="font-mono font-semibold text-slate-200">{a.code}</span>
                            <span className="text-[10px] uppercase text-slate-500 tracking-wider">{a.market}</span>
                            <span className={'text-[11px] ' + (a.direction === 'above' ? 'text-emerald-300' : 'text-rose-300')}>
                              {a.direction === 'above' ? '≥' : '≤'} {a.threshold}
                            </span>
                          </div>
                          {a.lastFiredAt && (
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              {isAr ? 'آخر إطلاق: ' : 'last fired: '}{new Date(a.lastFiredAt).toLocaleString()}
                            </div>
                          )}
                        </div>
                        <button onClick={function(){ removeAlert(a.id); }}
                                className="text-slate-500 hover:text-rose-400 text-[11px] px-2 py-1 rounded hover:bg-rose-500/10">
                          {isAr ? 'حذف' : 'Remove'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {!alerts && !loadErr && (
                <div className="text-[12px] text-slate-500">{isAr ? 'جارٍ التحميل…' : 'Loading…'}</div>
              )}
            </div>

            {/* Add form */}
            <div className="border-t border-white/5 pt-4">
              <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">
                {isAr ? 'إضافة تنبيه جديد' : 'Add New Alert'}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <label className="text-[11px] text-slate-400">
                  {isAr ? 'السوق' : 'Market'}
                  <select value={form.market}
                          onChange={function(e){ setForm(Object.assign({}, form, { market: e.target.value })); }}
                          className="mt-1 w-full px-2 py-1.5 rounded bg-slate-900/60 border border-white/10 text-slate-200 text-[12px] focus:outline-none focus:border-cyan-400/50">
                    {ALERT_MARKETS.map(function(m){ return <option key={m.code} value={m.code}>{m.label}</option>; })}
                  </select>
                </label>
                <label className="text-[11px] text-slate-400">
                  {isAr ? 'الرمز' : 'Ticker'}
                  <input type="text" value={form.code}
                         onChange={function(e){ setForm(Object.assign({}, form, { code: e.target.value })); }}
                         placeholder={isAr ? 'مثل ZAIN' : 'e.g. ZAIN'}
                         className="mt-1 w-full px-2 py-1.5 rounded bg-slate-900/60 border border-white/10 text-slate-200 text-[12px] font-mono uppercase placeholder-slate-600 focus:outline-none focus:border-cyan-400/50"/>
                </label>
                <label className="text-[11px] text-slate-400">
                  {isAr ? 'الاتجاه' : 'Direction'}
                  <select value={form.direction}
                          onChange={function(e){ setForm(Object.assign({}, form, { direction: e.target.value })); }}
                          className="mt-1 w-full px-2 py-1.5 rounded bg-slate-900/60 border border-white/10 text-slate-200 text-[12px] focus:outline-none focus:border-cyan-400/50">
                    <option value="above">{isAr ? 'أعلى من (≥)' : 'Above (≥)'}</option>
                    <option value="below">{isAr ? 'أقل من (≤)' : 'Below (≤)'}</option>
                  </select>
                </label>
                <label className="text-[11px] text-slate-400">
                  {isAr ? 'السعر المستهدف' : 'Target Price'}
                  <input type="number" step="any" value={form.threshold}
                         onChange={function(e){ setForm(Object.assign({}, form, { threshold: e.target.value })); }}
                         placeholder="0.00"
                         className="mt-1 w-full px-2 py-1.5 rounded bg-slate-900/60 border border-white/10 text-slate-200 text-[12px] font-mono placeholder-slate-600 focus:outline-none focus:border-cyan-400/50"/>
                </label>
              </div>
              {formErr && <div className="text-[11px] text-rose-400 mb-2">{formErr}</div>}
              <button onClick={addAlert} disabled={saving}
                      className="w-full px-3 py-2 rounded-md bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 text-cyan-300 text-[12px] font-medium disabled:opacity-50">
                {saving ? (isAr ? 'جارٍ الحفظ…' : 'Saving…') : (isAr ? '+ إضافة تنبيه' : '+ Add Alert')}
              </button>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

// ── Main App ─────────────────────────────────────────────────
function App() {
  var [data, setData] = useState(null);
  var [error, setError] = useState(null);
  var [lastUp, setLastUp] = useState(null);
  var [tab, setTab] = useState('overview'); // 'overview' | 'heatmap' | 'news' | 'calendar' | 'watchlist' | 'indices'
  var [allStocks, setAllStocks] = useState(null);
  var [sparks, setSparks] = useState(null); // 1M close series per index for sparklines
  var [sectorsByMarket, setSectorsByMarket] = useState(null); // { [market]: [sectors...] } for SectorHeatmap
  var [heatView, setHeatView] = useState('sectors'); // 'sectors' | 'markets' — heatmap tab sub-view
  var [lang, setLang] = useState(getInitialLang());
  var [alertsOpen, setAlertsOpen] = useState(false);
  var t = Object.assign({ lang: lang }, I18N[lang]);
  var timerRef = useRef(null);

  // Persist language and update document direction
  useEffect(function() {
    try { localStorage.setItem('marsad_lang', lang); } catch (e) {}
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
  }, [lang]);

  var tick = useCallback(async function() {
    try {
      var r = await fetch(GCC_API+'?action=hub',{cache:'no-store'});
      if (!r.ok) throw new Error('HTTP '+r.status);
      var j = await r.json();
      setData(j); setLastUp(Date.now()); setError(null);
    } catch(e) { setError(e.message||String(e)); }
  }, []);

  // Fetch the 1-month close series for all 7 indices once on mount and
  // every 5 minutes thereafter. This payload is tiny (~7 indices × 22
  // floats) and is cached for 5min on the edge so successive visitors
  // get it nearly free. Failures leave each Card's sparkline showing the
  // skeleton placeholder rather than crashing the page.
  useEffect(function() {
    var cancelled = false;
    function load() {
      fetch(GCC_API + '?action=indexcharts')
        .then(function(r){ return r.ok ? r.json() : null; })
        .then(function(j){
          if (!cancelled && j && Array.isArray(j.charts)) setSparks(j.charts);
        })
        .catch(function(){});
    }
    load();
    var id = setInterval(load, 5 * 60 * 1000);
    return function() { cancelled = true; clearInterval(id); };
  }, []);

  // Load search index for watchlist — lazy.
  // The search index is a ~90KB JSON of every GCC ticker. It's only
  // consumed by the Watchlist tab and the global search popover, so we
  // defer the fetch until the user actually needs it instead of paying
  // the cost on every overview page load. Fire-and-forget; the watchlist
  // tab shows a small "loading tickers…" state while this resolves.
  var [searchFetched, setSearchFetched] = useState(false);
  useEffect(function() {
    if (!searchFetched) return;
    fetch(GCC_API + '?action=search')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (d && d.stocks) setAllStocks(d.stocks);
      })
      .catch(function() {});
  }, [searchFetched]);
  // Sector heatmap fetch — lazy, fires the first time the heatmap tab is
  // opened, then revalidates every 5 minutes while the tab stays active.
  // 38-sector fan-out is ~4-7s cold (edge caches for 5min), so we only pay
  // it when the user actually wants the heatmap view.
  var [sectorsFetched, setSectorsFetched] = useState(false);
  useEffect(function() {
    if (tab === 'heatmap' && !sectorsFetched) setSectorsFetched(true);
  }, [tab, sectorsFetched]);
  useEffect(function() {
    if (!sectorsFetched) return;
    var cancelled = false;
    function load() {
      fetch(GCC_API + '?action=allsectors')
        .then(function(r){ return r.ok ? r.json() : null; })
        .then(function(j){
          if (!cancelled && j && j.markets) setSectorsByMarket(j.markets);
        })
        .catch(function(){});
    }
    load();
    var id = setInterval(load, 5 * 60 * 1000);
    return function() { cancelled = true; clearInterval(id); };
  }, [sectorsFetched]);

  // Trigger the search fetch the first time the user opens Watchlist,
  // and as a safety net after 6s of idle so search still works without
  // an extra interaction.
  useEffect(function() {
    if (tab === 'watchlist') setSearchFetched(true);
  }, [tab]);
  useEffect(function() {
    var tm = setTimeout(function() { setSearchFetched(true); }, 6000);
    return function() { clearTimeout(tm); };
  }, []);

  useEffect(function(){tick();},[tick]);
  useEffect(function(){
    if(timerRef.current)clearInterval(timerRef.current);
    timerRef.current=setInterval(tick,REFRESH_MS);
    return function(){clearInterval(timerRef.current);};
  },[tick]);
  useEffect(function(){
    function v(){if(document.visibilityState==='visible')tick();}
    document.addEventListener('visibilitychange',v);
    return function(){document.removeEventListener('visibilitychange',v);};
  },[tick]);

  var markets = data ? data.markets : [];

  return (
    <div className="min-h-screen text-slate-100 flex flex-col">
      {/* Header */}
      {/* HEADER — mirrors the News (/ and /ar) header pattern so all pages
          share the same brand bar. Logo asterisk + مرصد / MARSAD wordmark,
          then primary nav (Market is the active page here), then a language
          toggle pinned to the far edge. Uses the same #050e1a background
          and max-w-7xl container as the News page. */}
      <header className="bg-[#050e1a] border-b border-gray-800 sticky top-0 z-20" style={{paddingTop:'env(safe-area-inset-top)'}}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <a href={lang==='ar' ? '/ar.html' : '/'} className="flex items-center gap-2.5 shrink-0 no-underline" style={{textDecoration:'none'}}>
            <Logo size={38}/>
            <div className="leading-none">
              <div dir="rtl" lang="ar"
                   style={{fontFamily:"'IBM Plex Sans Arabic', 'Tajawal', sans-serif", fontWeight:700, fontSize:'20px', lineHeight:'22px', color:'#FFFFFF', letterSpacing:'0'}}>
                مرصد
              </div>
              <div lang="en"
                   style={{fontFamily:"'IBM Plex Sans', system-ui, sans-serif", fontWeight:700, fontSize:'11px', lineHeight:'13px', color:'#FFFFFF', letterSpacing:'0.22em', marginTop:'2px'}}>
                MARSAD
              </div>
            </div>
          </a>
          {/* Primary nav — Market (active) · News · Official Gazette.
              shrink-0 + whitespace-nowrap keep everything on one line. */}
          <nav className="flex items-center gap-3 sm:gap-5 ml-2 sm:ml-4 text-[12px] sm:text-sm font-semibold shrink-0 whitespace-nowrap"
               aria-label={lang==='ar'?'التنقل الرئيسي':'Primary'}
               style={lang==='ar'?{fontFamily:"'IBM Plex Sans Arabic', 'Tajawal', sans-serif"}:undefined}>
            {/* All items collapse to text-height so underline hugs the
                active label (same pattern as News / AR News pages). */}
            <a href={lang==='ar' ? '/ar.html' : '/'} className="text-gray-400 hover:text-white transition-colors no-underline" style={{textDecoration:'none', minHeight:0, lineHeight:1.2, padding:'0 0 2px 0', display:'inline-block', borderBottom:'2px solid transparent'}}>{lang==='ar' ? 'الأخبار' : 'News'}</a>
            <span className="text-white" aria-current="page" style={{minHeight:0, lineHeight:1.2, padding:'0 0 2px 0', display:'inline-block', borderBottom:'2px solid #fff'}}>{lang==='ar' ? 'الأسواق' : 'Markets'}</span>
            <a href="/gazette.html" className="text-gray-400 hover:text-white transition-colors no-underline" style={{textDecoration:'none', minHeight:0, lineHeight:1.2, padding:'0 0 2px 0', display:'inline-block', borderBottom:'2px solid transparent'}}>
              <span className="sm:hidden">{lang==='ar' ? 'الجريدة' : 'Gazette'}</span>
              <span className="hidden sm:inline">{lang==='ar' ? 'الجريدة الرسمية' : 'Official Gazette'}</span>
            </a>
          </nav>
          {/* Right-side controls — language toggle. min-height 40px + px-3
              so it matches the size of the EN↔AR toggle on the News page
              (which is naturally tall because it sits next to the DEFCON
              card and items-stretch pulls it to that height). */}
          <div className="flex items-stretch gap-1.5 sm:gap-2 ml-auto">
            {/* Price-alerts bell. Opens a modal with list+add form; push
                permission is requested lazily inside the modal. */}
            <button onClick={function(){ setAlertsOpen(true); }}
               title={lang==='ar' ? 'تنبيهات الأسعار' : 'Price alerts'}
               aria-label={lang==='ar' ? 'تنبيهات الأسعار' : 'Price alerts'}
               className="inline-flex items-center justify-center px-3 rounded-lg border border-gray-700 bg-gray-800 text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
               style={{minHeight:'40px', minWidth:'40px', fontSize:'16px'}}>
              🔔
            </button>
            <button onClick={function(){ setLang(lang==='ar'?'en':'ar'); }}
               title={lang==='ar' ? 'English version' : 'النسخة العربية'}
               aria-label={lang==='ar' ? 'English version' : 'النسخة العربية'}
               className="inline-flex items-center justify-center px-3 rounded-lg border border-gray-700 bg-gray-800 text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
               style={{fontFamily:"'IBM Plex Sans Arabic', 'IBM Plex Sans', system-ui, sans-serif", fontWeight:600, fontSize:'11px', lineHeight:'13px', letterSpacing:'0.04em', minHeight:'40px', minWidth:'40px'}}>
              {lang==='ar' ? 'EN' : 'عربي'}
            </button>
          </div>
        </div>
      </header>

      {/* Title + Search */}
      <section className="max-w-6xl mx-auto w-full px-4 sm:px-6 pt-6 pb-2">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight"
                style={lang==='ar'?{fontFamily:"'IBM Plex Sans Arabic','IBM Plex Sans',sans-serif"}:undefined}>
              {t.title}
            </h1>
            <div className="text-[13px] text-slate-500 mt-0.5"
                 dir={lang==='ar'?'ltr':'rtl'} lang={lang==='ar'?'en':'ar'}
                 style={{fontFamily:lang==='ar'?undefined:"'IBM Plex Sans Arabic',sans-serif"}}>
              {lang==='ar' ? 'GCC Markets' : 'أسواق الخليج'}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Language toggle */}
            <button
              onClick={function(){ setLang(lang==='ar'?'en':'ar'); }}
              className="text-[11px] px-2.5 py-1 rounded border border-white/10 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors font-medium"
              title="Switch language">
              {lang==='ar' ? 'EN' : 'عربي'}
            </button>
            {lastUp && (
              <div className="text-[10px] text-slate-600 font-mono flex items-center gap-2">
                <span title="Card metrics (breadth, volume, top movers) come from mubasher.info and are typically delayed ~15 min during market hours. The live ticker above streams real-time prices via TradingView.">
                  {t.updated} {Math.floor((Date.now()-lastUp)/1000)}s {t.ago} · {t.cardsDelayed}
                </span>
                <button onClick={tick} className="px-2 py-1 rounded border border-white/8 hover:border-cyan-400/30 hover:text-cyan-300 transition-colors">↻</button>
              </div>
            )}
          </div>
        </div>
        {/* Live ticker only shows when ≥1 market is open (avoids the
            cluttered ⚠️ "no live data" state TradingView renders when
            GCC markets are all closed). After hours users see the cards
            directly; the All Indices tab still shows the full TV widget. */}
        {markets.some(function(m){ return m.isOpen; }) && <TVTickerTape/>}
        {/* Macro strip (Brent/WTI/Gold/DXY/US 10Y/KWD/BTC) — always mounted,
            not tab-scoped. Gives a GCC reader cross-asset context above
            the index cards on every view. */}
        <MacrosStrip/>
        <GlobalSearch t={t}/>
      </section>

      {/* Tabs */}
      <section className="max-w-6xl mx-auto w-full px-4 sm:px-6 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-4 overflow-x-auto">
          <button onClick={function(){setTab('overview');}} className={'tab-btn '+(tab==='overview'?'tab-btn-active':'')}>
            {t.overview}
          </button>
          <button onClick={function(){setTab('heatmap');}} className={'tab-btn '+(tab==='heatmap'?'tab-btn-active':'')}>
            {t.heatmap}
          </button>
          <button onClick={function(){setTab('news');}} className={'tab-btn '+(tab==='news'?'tab-btn-active':'')}>
            {t.news}
          </button>
          <button onClick={function(){setTab('calendar');}} className={'tab-btn '+(tab==='calendar'?'tab-btn-active':'')}>
            {t.calendar}
          </button>
          <button onClick={function(){setTab('disclosures');}} className={'tab-btn '+(tab==='disclosures'?'tab-btn-active':'')}>
            {t.disclosures}
          </button>
          <button onClick={function(){setTab('watchlist');}} className={'tab-btn '+(tab==='watchlist'?'tab-btn-active':'')}>
            {t.watchlist}
          </button>
          <button onClick={function(){setTab('indices');}} className={'tab-btn '+(tab==='indices'?'tab-btn-active':'')}>
            {t.indices}
          </button>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-6xl mx-auto w-full px-4 sm:px-6 pb-8 flex-1">

        {/* Overview tab */}
        {tab === 'overview' && (
          <React.Fragment>
            <DailyBrief lang={lang}/>
            {data && <Summary markets={markets} t={t}/>}
            {data && data.leaderboards && <Leaderboards leaderboards={data.leaderboards} t={t}/>}
            {error && !data && (
              <div className="gcc-card p-6 text-center text-rose-400 text-sm">
                <div className="font-semibold mb-1">Failed to load market data</div>
                <div className="text-xs text-slate-500">{error}</div>
                <button onClick={tick} className="mt-3 px-4 py-2 rounded-lg border border-white/10 hover:border-cyan-400/40 hover:text-cyan-300 text-xs">Retry</button>
              </div>
            )}
            {!data && !error && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {[0,1,2,3,4,5,6].map(function(i){return <div key={i} className="gcc-card p-4 h-[200px] gcc-breath flex items-center justify-center"><span className="text-slate-600 text-xs">Loading…</span></div>;})}
              </div>
            )}
            {data && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {markets.map(function(m){
                  var seriesEntry = sparks ? sparks.find(function(c){ return c.market === m.market && c.index === m.index; }) : null;
                  var series = seriesEntry ? seriesEntry.closes : null;
                  return <Card key={m.market} m={m} t={t} spark={series}/>;
                })}
              </div>
            )}
          </React.Fragment>
        )}

        {/* Heatmap tab */}
        {tab === 'heatmap' && (
          <React.Fragment>
            {error && !data && (
              <div className="gcc-card p-6 text-center text-rose-400 text-sm">
                <div className="font-semibold mb-1">Failed to load market data</div>
                <div className="text-xs text-slate-500">{error}</div>
              </div>
            )}
            {!data && !error && (
              <div className="text-slate-500 text-sm mb-4">Loading heatmap…</div>
            )}
            {data && (
              <React.Fragment>
                {/* Heatmap view toggle: Sectors (default, 38 sub-indices) vs. Markets (7 tiles). */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" role="tablist">
                    <button
                      onClick={function(){setHeatView('sectors');}}
                      className={'px-3 py-1.5 text-xs font-medium transition-colors ' +
                        (heatView === 'sectors'
                          ? 'bg-cyan-500/15 text-cyan-300'
                          : 'bg-transparent text-slate-400 hover:text-slate-200')}>
                      Sectors
                    </button>
                    <button
                      onClick={function(){setHeatView('markets');}}
                      className={'px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-700 ' +
                        (heatView === 'markets'
                          ? 'bg-cyan-500/15 text-cyan-300'
                          : 'bg-transparent text-slate-400 hover:text-slate-200')}>
                      Markets
                    </button>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {heatView === 'sectors'
                      ? 'Sub-indices across GCC — color intensity scales with % move (saturates at ±4%).'
                      : t.heatmapHint}
                  </div>
                </div>
                {heatView === 'sectors'
                  ? <SectorHeatmap sectorsByMarket={sectorsByMarket} markets={markets} t={t}/>
                  : <Heatmap markets={markets}/>}
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        {/* News tab */}
        {tab === 'news' && (
          <React.Fragment>
            <div className="text-[12px] text-slate-500 mb-4">
              {t.newsHint}
            </div>
            {data && <NewsFeed headlines={data.headlines||[]} t={t}/>}
            {!data && <div className="text-slate-500 text-sm">{t.loading}</div>}
          </React.Fragment>
        )}

        {/* Calendar tab */}
        {tab === 'calendar' && (
          <React.Fragment>
            <div className="text-[12px] text-slate-500 mb-4">
              {t.calendarHint}
            </div>
            {data && <Calendar events={data.events||[]} t={t}/>}
            {!data && <div className="text-slate-500 text-sm">{t.loading}</div>}
          </React.Fragment>
        )}

        {/* Disclosures tab */}
        {tab === 'disclosures' && (
          <React.Fragment>
            <div className="text-[12px] text-slate-500 mb-4">
              {t.disclosuresHint}
            </div>
            <DisclosuresFeed lang={lang} t={t}/>
          </React.Fragment>
        )}

        {/* Watchlist tab */}
        {tab === 'watchlist' && (
          <Watchlist allStocks={allStocks} t={t}/>
        )}

        {/* All Indices tab */}
        {tab === 'indices' && (
          <React.Fragment>
            <div className="text-[12px] text-slate-500 mb-4">
              {t.indicesHint}
            </div>
            <TVMarketOverview visible={tab === 'indices'}/>
          </React.Fragment>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#0A0A0F] mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2.5">
              <Logo size={18}/>
              <span className="text-[10px] text-slate-600 uppercase tracking-wider">Marsad · GCC Markets</span>
            </div>
            <div className="text-[10px] text-slate-700 italic" style={{fontFamily:"'IBM Plex Sans Arabic','IBM Plex Sans',sans-serif"}}>
              ما يجري، كما يجري / What's happening, as it happens.
            </div>
          </div>
          <div className="text-[10px] text-slate-600 font-mono">
            Data: mubasher.info · TradingView
          </div>
        </div>
      </footer>

      {/* Alerts modal — renders as a fixed overlay when open. */}
      <AlertsModal open={alertsOpen} onClose={function(){ setAlertsOpen(false); }} lang={lang} t={t}/>
    </div>
  );
}

// ── Mount ────────────────────────────────────────────────────
const container = document.getElementById('root');
const root = ReactDOM.createRoot(container);
root.render(<App/>);
var splash = document.getElementById('marsad-boot-splash');
if (splash) {
  var shownAt = window.__marsadSplashShownAt || Date.now();
  var elapsed = Date.now() - shownAt;
  var wait = Math.max(600 - elapsed, 100);
  setTimeout(function() {
    splash.classList.add('marsad-splash-hide');
    setTimeout(function() { if (splash && splash.parentNode) splash.parentNode.removeChild(splash); }, 400);
  }, wait);
}

})();

